// 一级页面通用容器
import './PageContainer.less';

import classNames from 'classnames';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useI18nContext } from '@/i18n/I18n';
import { useUserStore } from '@/store/useUser';
import { isOAuthHeader, NO_HEADER_H5_PATH } from '@/utils/constants';
import isApp, { isMobile } from '@/utils/ua';

import firstLevelRoutesList from '../../routes/FirstLevelRoutes';

const OAuthHeader = lazy(() => import('../OAuthHeader'));
const AccountMenu = lazy(() => import('./AccountMenu'));

export enum PageLevel {
  main = 1,
  sub = 2,
}

interface IPageContainerProps {
  pageLevel: PageLevel;
  children: React.ReactNode;
  pageWrapperClass?: string;
}

const PageContainer = ({
  pageLevel,
  pageWrapperClass,
  children,
}: IPageContainerProps) => {
  const [, lang] = useI18nContext();
  const [collapse, setCollapse] = useState(false);
  const navigate = useNavigate();

  const location = useLocation();

  const { RegulatedSiteHosts } = useUserStore();

  useEffect(() => {
    if (location.pathname.includes('/app/user/sub-account-login')) {
      document.documentElement.setAttribute('hide-security-find-entry', '1');
    } else {
      document.documentElement.removeAttribute('hide-security-find-entry');
    }
  }, [location]);

  const isShowHeader =
    (!isMobile() ||
      NO_HEADER_H5_PATH.every(
        (pathname: string) => location.pathname !== pathname,
      )) &&
    !isApp();

  const supportH5Page = useMemo(() => {
    const supportH5PagePath = [
      '/app/user/auto-ban-account',
      '/app/user/api-management',
      '/app/user/edit-secret-key',
      '/app/user/add-secret-key',
      '/app/user/delete-account',
    ];
    const currentPathSupportH5 = supportH5PagePath.some((pathname) =>
      location.pathname.includes(pathname),
    );
    return currentPathSupportH5 && isMobile({ tablet: true });
  }, [location.pathname]);

  return (
    <>
      <Suspense fallback={''}>{isOAuthHeader() && <OAuthHeader />}</Suspense>
      <div
        className={classNames(
          'by-us-page',
          (isMobile() || isApp()) && 'by-us-page-h5',
        )}
      >
        {pageLevel === PageLevel.main && isShowHeader && (
          <div className="by-us-menu">
            <Suspense fallback={''}>
              {!(isMobile() && supportH5Page) && (
                <AccountMenu
                  locale={lang}
                  regulatedSiteHosts={RegulatedSiteHosts}
                  navigate={({ href }) => {
                    if (
                      firstLevelRoutesList.some(
                        ({ path }) => `/app/${path}` === href,
                      )
                    ) {
                      navigate(href, {
                        replace: true,
                      });
                    } else {
                      window.location.href = href;
                    }
                  }}
                />
              )}
            </Suspense>
          </div>
        )}
        <div
          className={classNames(
            'page-wrapper',
            !supportH5Page && isShowHeader && 'page-wrapper-min-width',
            pageLevel === PageLevel.sub && 'sub-page-wrapper',
            pageWrapperClass || '',
          )}
          style={
            pageLevel === PageLevel.main
              ? {
                  width: collapse
                    ? `calc(100vw - 54px)`
                    : `calc(100vw - 250px)`,
                }
              : {}
          }
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default PageContainer;
